import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDescriptionTasks } from "../../../../../store/slices/DescriptionTask";
import {
  setAssociateInstallation,
  setAssoInstaPiechart,
} from "../../../../../store/slices/salesFinanceSlice";
import { fetchSalesTaxes } from "../../../../../store/slices/salesTaxesSlice";
import { fetchQuotations } from "../../../../../store/slices/hourlyRates";
import { fetchOtherExpenses } from "../../../../../store/slices/otherexpenses";
import { fetchEmployees } from "../../../../../store/slices/employeeAdminSlice";
import { fetchAllProjects } from "../../../../../store/slices/projectsSlice";
import { getMyTrainings } from "../../../../../store/slices/MyTraining/myTrainings";
import { useCurrencySymbol } from "../../../../../utils/currencyUtils";
import CommaConvert from "../../../../../utils/CommaConvert";
import {
  parseAmountNumber,
  formatAmountComma,
} from "../../../../../utils/decimalAmountUtils";
import {
  canonicalInstallationAssociatedTaskDescription,
  filterInstallationAssociatedExpenseEntriesForAggregation,
  getInstallationActualTrainingDisplayAmount,
  isTrainingExpenseDisplayRow,
} from "../../../../../utils/trainingOtherExpenseSync";
import {
  getLoggedSalesUserId,
  getPerdiemAndAverageCostFromExpenses,
  scopeOtherExpensesForUser,
} from "../../../../../utils/salespersonRatesScope";

/** Project-tracking description rows may use `value` while quoted path uses `expenses`. */
const installationAssociatedEntryAmount = (entry) =>
  parseAmountNumber(entry?.expenses ?? entry?.value ?? entry?.Value);

const getLeadRowKey = (row) => {
  if (row?.quotationId != null && String(row.quotationId).trim() !== "") {
    return String(row.quotationId).trim();
  }
  return String(row?.ProjectName ?? "").trim();
};

const isWonOrActualLead = (row) => {
  if (!row) return false;
  const status = String(row?.Status ?? row?.status ?? "").trim().toLowerCase();
  if (status === "won" || status === "won recurring") return true;
  if (typeof row?.isProjectQuoted === "boolean") return row.isProjectQuoted;
  if (row?.isProjectQuoted === 1) return true;
  if (String(row?.isProjectQuoted || "").toLowerCase() === "true") return true;
  return false;
};

/** Freeze perdiem/avgcost per lead once read from project snapshot. */
const actualInstallationTableRatesLock = new Map();

/** Read locked perdiem + avgcost from non-tracking quoted installation project twin. */
function findQuotedInstallationTwin(allProjects, trackingInstallRow, leadRow) {
  const list = allProjects || [];
  const isInstall = (p) =>
    String(p?.TypeService || "").toLowerCase().includes("installation");

  if (trackingInstallRow) {
    const twin = list.find(
      (p) =>
        p.isProjecttracking === false &&
        isInstall(p) &&
        String(p.quotationId ?? "").trim() !== "" &&
        String(p.quotationId ?? "").trim() ===
          String(trackingInstallRow.quotationId ?? "").trim() &&
        String(p.TypeService ?? "").trim() ===
          String(trackingInstallRow.TypeService ?? "").trim()
    );
    if (twin) return twin;
  }

  const selQId =
    leadRow?.quotationId != null && String(leadRow.quotationId).trim() !== ""
      ? String(leadRow.quotationId).trim()
      : "";
  const selName = String(leadRow?.ProjectName || "").trim();

  return (
    list.find((p) => {
      if (p.isProjecttracking !== false || !isInstall(p)) return false;
      if (
        selQId &&
        p.quotationId != null &&
        String(p.quotationId).trim() === selQId
      ) {
        return true;
      }
      return selName && String(p.ProjectName || "").trim() === selName;
    }) || null
  );
}

function ratesFromProjectSnapshot(projectRow) {
  if (!projectRow) return null;
  const perdiem = Number(projectRow.perdiem) || 0;
  const averagecost = Number(projectRow.avgcost ?? projectRow.averagecost) || 0;
  // Empty twin must not lock 0/0 (QA often has twin without rates; localhost may not).
  if (!perdiem && !averagecost) return null;
  return { perdiem, averagecost };
}

/** Max trip duration across a person's installation description entries. */
const tripDurationFromEntries = (entries = []) =>
  (entries || []).reduce(
    (max, e) => Math.max(max, Number(e?.tripduration) || 0),
    0
  );

/**
 * Resolve trip days for a person column. Prefer their own entries; if missing
 * (common for installation manager on QA), fall back to max trip on the project
 * so Perdiem / Average Cost use the same days as other assignees.
 */
const resolveGroupTripDuration = (group, allGroups = []) => {
  const own = tripDurationFromEntries(group?.entries);
  if (own > 0) return own;
  return (allGroups || []).reduce(
    (max, g) => Math.max(max, tripDurationFromEntries(g?.entries)),
    0
  );
};

const ActualInstallationTable = ({
  filteredQuotation,
  matchedProjects,
  currentPerson,
  AssociatedRef,
  tasks,
  selectedProject,
  perdiemRate: perdiemRateProp,
  averageCostRate: averageCostRateProp,
}) => {

  const dispatch = useDispatch();
  const currency = useCurrencySymbol();
  const USAComma = CommaConvert();
  const formatMoneyComma = formatAmountComma;
  const { employees } = useSelector((state) => state.employees);
  const { mytrainings } = useSelector((state) => state.myTrainings);
  const { projects } = useSelector((state) => state.projects);
  const [perdiem, setperdiem] = useState(0);
  const [averagecost, setaveragecost] = useState(0);

  const { items: otherExpenses } = useSelector((state) => state.otherExpenses);
  const loggedInUserId = useSelector((state) => state.leads.loggedInUserId);
  const email = typeof localStorage !== "undefined" ? localStorage.getItem("email") : "";
  const loggedUserId = useMemo(
    () => getLoggedSalesUserId(loggedInUserId, employees, email),
    [loggedInUserId, employees, email]
  );
  const [, setFsfasfas] = useState([]);

  const [matchedEntries, setMatchedEntries] = useState([]);
  const [filteredMatchedEntries, setFilteredMatchedEntries] = useState([]);
  useEffect(() => {
    dispatch(fetchDescriptionTasks());
    dispatch(fetchSalesTaxes());
    dispatch(fetchQuotations());
    dispatch(fetchOtherExpenses());
    dispatch(fetchEmployees());
    dispatch(fetchAllProjects());
    dispatch(getMyTrainings());
  }, [dispatch, tasks]);

  useEffect(() => {
    const matchedProjectIds = matchedProjects
      .filter(
        (project) =>
          project.TypeService &&
          project.TypeService.toLowerCase().includes("installation")
      )
      .map((project) => project._id);

    const matchedDescription = tasks
      .filter((task) => matchedProjectIds.includes(task.projectid))
      .sort((a, b) => {
        if (a.projectid < b.projectid) return -1;
        if (a.projectid > b.projectid) return 1;
        return 0;
      });

    setFsfasfas(matchedDescription);
    const filteredMatchedDescription = matchedDescription.filter((task) => {
      const employeeMatch = employees.find(
        (emp) => emp._id === task.taskownerid
      );
      return employeeMatch;
    });

    const groupedByTaskOwner = filteredMatchedDescription.reduce(
      (acc, task) => {
        const ownerId = task.taskownerid;
        if (!acc[ownerId]) {
          acc[ownerId] = [];
        }
        acc[ownerId].push(task);
        return acc;
      },
      {}
    );

    const groupedArray = Object.entries(groupedByTaskOwner).map(
      ([taskownerid, entries]) => {
        // Get descriptions that appear in all persons
        const allDescriptions = Object.values(groupedByTaskOwner).flatMap(
          (entries) => entries.map((entry) => entry.description)
        );

        // Count frequency of each description
        const descriptionFrequency = {};
        allDescriptions.forEach((desc) => {
          descriptionFrequency[desc] = (descriptionFrequency[desc] || 0) + 1;
        });

        // Get the total number of persons
        const totalPersons = Object.keys(groupedByTaskOwner).length;

        // Sort entries: descriptions that appear in all persons first, then others
        const sortedEntries = [...entries].sort((a, b) => {
          const appearsInAllA =
            descriptionFrequency[a.description] === totalPersons;
          const appearsInAllB =
            descriptionFrequency[b.description] === totalPersons;

          // If both appear in all persons or both don't, sort alphabetically
          if (appearsInAllA && !appearsInAllB) return -1;
          if (!appearsInAllA && appearsInAllB) return 1;

          // If same category, sort alphabetically
          return a.description.localeCompare(b.description);
        });

        const updatedEntries = sortedEntries.map((entry) => {
          return {
            ...entry,
            perdiem: perdiem,
            averagecost: averagecost,
          };
        });

        return {
          taskownerid,
          entries: updatedEntries,
        };
      }
    );

    setMatchedEntries(groupedArray);
  }, [tasks, employees, matchedProjects, averagecost, perdiem]);

  // Get person display name with proper numbering
  // Get person display name with manager-specific numbering
  const getPersonDisplayName = useMemo(() => {
    return (taskownerid, groupedPersons) => {
      // Convert currentPerson to array for consistent processing
      const persons = Array.isArray(currentPerson)
        ? currentPerson
        : [currentPerson];

      const personData = persons.find(
        (person) => person.personId === taskownerid
      );
      const currentEmployee = employees.find((emp) => emp._id === taskownerid);

      if (personData) {
        // For grouped display, use the pre-calculated display name from grouping logic
        const groupedPerson = groupedPersons?.find(
          (p) => p.personId === taskownerid
        );
        if (groupedPerson?.displayName) {
          return groupedPerson.displayName;
        }

        // Fallback to individual logic if no grouped data
        const serviceType = personData.typeService || "Installation";
        const role = personData.roles?.[0] || "";

        if (role === "Project manager") {
          // Find manager number
          const managers = persons.filter(
            (p) => p.roles?.[0] === "Project manager"
          );
          const managerNumber =
            managers.findIndex((p) => p.personId === personData.personId) + 1;
          return `${serviceType} Manager ${managerNumber}`;
        } else if (role === "Operations") {
          // Find which manager this operations person belongs to and their number within that manager
          const managers = persons.filter(
            (p) => p.roles?.[0] === "Project manager"
          );
          const operations = persons.filter(
            (p) => p.roles?.[0] === "Operations"
          );

          // Group operations by manager (same ProjectID)
          const groupedByManager = managers.map((manager) => {
            const managerOperations = operations.filter(
              (op) => op.ProjectID === manager.ProjectID
            );
            return {
              manager,
              operations: managerOperations,
            };
          });

          for (const group of groupedByManager) {
            const operationIndex = group.operations.findIndex(
              (op) => op.personId === personData.personId
            );
            if (operationIndex !== -1) {
              // Find manager number for this group
              const managerNumber =
                managers.findIndex(
                  (p) => p.personId === group.manager.personId
                ) + 1;
              return `${serviceType} Employee ${managerNumber}.${operationIndex + 1
                }`;
            }
          }

          // Fallback if no manager found
          return `${serviceType} Employee`;
        } else {
          return `${serviceType} ${role}`;
        }
      }

      // Fallback to employee data if personData not available
      return currentEmployee
        ? `${currentEmployee?.servicetype} ${currentEmployee.customRoles?.[0] || "No Role"
        }`
        : "Unknown";
    };
  }, [currentPerson, employees]);

  const groupPersonsByManager = useMemo(() => {
    if (!currentPerson) return [];

    const persons = Array.isArray(currentPerson)
      ? currentPerson
      : [currentPerson];

    // Find all managers across all projects
    const allManagers = persons.filter((p) =>
      p.roles?.includes("Project manager")
    );
    const allOperations = persons.filter((p) =>
      p.roles?.includes("Operations")
    );

    // Sort managers to ensure consistent ordering
    const sortedManagers = allManagers.sort((a, b) => {
      const projectCompare = (a.projectName || "").localeCompare(
        b.projectName || ""
      );
      if (projectCompare !== 0) return projectCompare;
      return (a.personName || "").localeCompare(b.personName || "");
    });

    const result = [];

    // Process each manager globally
    sortedManagers.forEach((manager, managerIndex) => {
      const managerNumber = managerIndex + 1;

      // Add manager with global numbering
      result.push({
        ...manager,
        displayName: `${manager.typeService || "Installation"
          } Manager ${managerNumber}`,
        isManager: true,
        managerGroup: `manager_${managerNumber}`,
      });

      // Find operations personnel for this manager (same ProjectID)
      const operationsForManager = allOperations.filter(
        (op) => op.ProjectID === manager.ProjectID
      );

      // Match Project Cost table: keep installation employee order from currentPerson (AllServiceTable rows)
      const sortedOperations = [...operationsForManager].sort((a, b) => {
        const indexA = persons.findIndex((p) => p.personId === a.personId);
        const indexB = persons.findIndex((p) => p.personId === b.personId);
        const orderA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
        const orderB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
        return orderA - orderB;
      });

      // Add operations personnel with manager-specific numbering
      sortedOperations.forEach((ops, opsIndex) => {
        const opsNumber = opsIndex + 1;
        result.push({
          ...ops,
          displayName: `${ops.typeService || "Installation"
            } Employee ${opsNumber}`,
          isManager: false,
          managerGroup: `manager_${managerNumber}`,
        });
      });
    });

    return result;
  }, [currentPerson]);

  // Handle both single person object and array of persons with proper grouping
  useEffect(() => {
    if (currentPerson && matchedEntries.length > 0) {
      const groupedPersons = groupPersonsByManager;

      // Extract all person IDs from grouped persons
      const personIds = groupedPersons.map((person) => person.personId);

      // Filter matchedEntries to include only those with taskownerid in personIds
      const filtered = matchedEntries.filter((group) =>
        personIds.includes(group.taskownerid)
      );

      // Sort the filtered entries according to the grouped persons order
      const sortedFiltered = filtered.sort((a, b) => {
        const indexA = groupedPersons.findIndex(
          (p) => p.personId === a.taskownerid
        );
        const indexB = groupedPersons.findIndex(
          (p) => p.personId === b.taskownerid
        );
        return indexA - indexB;
      });

      setFilteredMatchedEntries(sortedFiltered);
    } else {
      setFilteredMatchedEntries([]);
    }
  }, [matchedEntries, currentPerson, groupPersonsByManager]);

  const lockedRatesFromItemizedPersons = useMemo(() => {
    const persons = Array.isArray(currentPerson)
      ? currentPerson
      : currentPerson
        ? [currentPerson]
        : [];
    const rowWithRates = persons.find(
      (person) =>
        person?.installationPerdiemRate != null ||
        person?.installationAveragecostRate != null
    );
    if (!rowWithRates) return null;
    return {
      perdiem: Number(rowWithRates.installationPerdiemRate) || 0,
      averagecost: Number(rowWithRates.installationAveragecostRate) || 0,
    };
  }, [currentPerson]);

  /** Won / actual P&L: perdiem + avgcost from project document (quoted non-tracking twin). */
  const lockedRatesFromProjectSnapshot = useMemo(() => {
    const rowKey = getLeadRowKey(selectedProject);
    if (rowKey && actualInstallationTableRatesLock.has(rowKey)) {
      const cached = actualInstallationTableRatesLock.get(rowKey);
      if ((cached?.perdiem || 0) > 0 || (cached?.averagecost || 0) > 0) {
        return cached;
      }
      actualInstallationTableRatesLock.delete(rowKey);
    }

    const trackingInstall = (matchedProjects || []).find((p) =>
      String(p?.TypeService || "").toLowerCase().includes("installation")
    );
    const quotedTwin = findQuotedInstallationTwin(
      projects,
      trackingInstall,
      selectedProject
    );
    const fromProject = ratesFromProjectSnapshot(quotedTwin);
    if (!fromProject) return null;

    if (rowKey) {
      actualInstallationTableRatesLock.set(rowKey, fromProject);
    }
    return fromProject;
  }, [selectedProject, matchedProjects, projects]);

  const shouldUseProjectLockedRates =
    isWonOrActualLead(selectedProject) || lockedRatesFromProjectSnapshot != null;

  const useLiveOtherExpenses =
    !shouldUseProjectLockedRates &&
    lockedRatesFromItemizedPersons == null &&
    (perdiemRateProp === undefined || averageCostRateProp === undefined);

  useEffect(() => {
    const rowKey = getLeadRowKey(selectedProject);

    if (rowKey && actualInstallationTableRatesLock.has(rowKey)) {
      const cached = actualInstallationTableRatesLock.get(rowKey);
      if ((cached?.perdiem || 0) > 0 || (cached?.averagecost || 0) > 0) {
        setperdiem(cached.perdiem);
        setaveragecost(cached.averagecost);
        return;
      }
      actualInstallationTableRatesLock.delete(rowKey);
    }

    if (lockedRatesFromProjectSnapshot) {
      if (rowKey) {
        actualInstallationTableRatesLock.set(
          rowKey,
          lockedRatesFromProjectSnapshot
        );
      }
      setperdiem(lockedRatesFromProjectSnapshot.perdiem);
      setaveragecost(lockedRatesFromProjectSnapshot.averagecost);
      return;
    }

    if (lockedRatesFromItemizedPersons) {
      setperdiem(lockedRatesFromItemizedPersons.perdiem);
      setaveragecost(lockedRatesFromItemizedPersons.averagecost);
      return;
    }
    if (perdiemRateProp !== undefined && averageCostRateProp !== undefined) {
      setperdiem(Number(perdiemRateProp) || 0);
      setaveragecost(Number(averageCostRateProp) || 0);
      return;
    }

    if (!useLiveOtherExpenses) return;

    const expensesForUser = scopeOtherExpensesForUser(otherExpenses, {
      loggedUserId,
    });
    const { perdiem: p, averagecost: a } =
      getPerdiemAndAverageCostFromExpenses(expensesForUser);
    setperdiem(p);
    setaveragecost(a);
  }, [
    selectedProject,
    lockedRatesFromProjectSnapshot,
    perdiemRateProp,
    averageCostRateProp,
    lockedRatesFromItemizedPersons,
    useLiveOtherExpenses,
    otherExpenses,
    loggedUserId,
  ]);

  const actualInstallationTrainingForGroup = useCallback(
    (group) => {
      const assigneeEmp = employees.find((e) => String(e._id) === String(group.taskownerid));
      return getInstallationActualTrainingDisplayAmount({
        entries: group.entries,
        matchedProjects,
        assigneeEmp,
        trainingsEnvelope: mytrainings,
        projects,
        currencySymbol: currency,
      });
    },
    [employees, matchedProjects, mytrainings, projects, currency]
  );

  const AssoInstaPiechart = useMemo(() => {
    if (!filteredMatchedEntries?.length) return {};

    const chartData = {};
    filteredMatchedEntries.forEach((group) => {
      const trainKey = canonicalInstallationAssociatedTaskDescription(
        `Training Cost (${currency})`,
        currency
      );
      chartData[trainKey] =
        (chartData[trainKey] || 0) + actualInstallationTrainingForGroup(group);

      filterInstallationAssociatedExpenseEntriesForAggregation(group.entries).forEach((item) => {
        if (isTrainingExpenseDisplayRow(item)) return;
        const key = canonicalInstallationAssociatedTaskDescription(item.description, currency);
        const value = installationAssociatedEntryAmount(item);
        chartData[key] = (chartData[key] || 0) + value;
      });
    });

    const total = Object.values(chartData).reduce((sum, v) => sum + v, 0);
    chartData["Total"] = total;

    const totalTripDuration = filteredMatchedEntries.reduce(
      (sum, group) => sum + (group.entries[0]?.tripduration ?? 0),
      0
    );
    chartData["Trip Duration"] = totalTripDuration;

    return chartData;
  }, [filteredMatchedEntries, currency, actualInstallationTrainingForGroup]);

  useEffect(() => {
    if (AssoInstaPiechart) {
      dispatch(setAssoInstaPiechart(AssoInstaPiechart));
    }
  }, [dispatch, AssoInstaPiechart]);

  const [totalDataRow, setTotalDataRow] = useState({});

  useEffect(() => {
    dispatch(setAssociateInstallation(totalDataRow.Total));
  }, [totalDataRow, dispatch]);

  useEffect(() => {
    // const dynamicDescriptions = Array.from(
    //     new Set(
    //         filteredMatchedEntries.flatMap((group) =>
    //             group.entries.map((entry) => entry.description)
    //         )
    //     )
    // );

    // const staticHeaders = ["Task Owner ID", "Trip Duration"];
    // const footerFields = [`Perdiem (${currency})`, `Average Cost (${currency})`];

    // const finalColumns = [...staticHeaders, ...dynamicDescriptions, ...footerFields, "Total"];

    // Compute Total Row for all filtered persons
    const totalRow = {};

    if (filteredMatchedEntries.length > 0) {
      totalRow["Task Owner ID"] = "Total";

      let totalAssociatedCost = 0;

      filteredMatchedEntries.forEach((group) => {
        const tripduration = resolveGroupTripDuration(
          group,
          filteredMatchedEntries
        );

        // Calculate Perdiem and Average Cost for this group
        const groupPerdiem = perdiem * tripduration;
        const groupAvgCost = averagecost * tripduration;

        // Calculate Associated Cost for this group
        let groupAssociatedCost = actualInstallationTrainingForGroup(group);
        group.entries.forEach((entry) => {
          if (isTrainingExpenseDisplayRow(entry)) return;
          const amt = installationAssociatedEntryAmount(entry);
          if (amt) {
            groupAssociatedCost += amt;
          }
        });
        groupAssociatedCost += groupPerdiem + groupAvgCost;

        totalAssociatedCost += groupAssociatedCost;
      });

      totalRow["Total"] = totalAssociatedCost.toFixed(2);
    }

    setTotalDataRow(totalRow);
  }, [filteredMatchedEntries, perdiem, averagecost, actualInstallationTrainingForGroup]);

  // Get all unique descriptions across all persons, sorted by common ones first
  const getAllDescriptions = () => {
    const orderedUnique = [];
    const seen = new Set();
    filteredMatchedEntries.forEach((group) => {
      group.entries.forEach((entry) => {
        const c = canonicalInstallationAssociatedTaskDescription(entry.description, currency);
        if (!seen.has(c)) {
          seen.add(c);
          orderedUnique.push(c);
        }
      });
    });

    const descriptionFrequency = {};
    filteredMatchedEntries.forEach((group) => {
      const labels = new Set(
        group.entries.map((e) => canonicalInstallationAssociatedTaskDescription(e.description, currency))
      );
      labels.forEach((c) => {
        descriptionFrequency[c] = (descriptionFrequency[c] || 0) + 1;
      });
    });

    return orderedUnique.sort((a, b) => {
      const appearsInAllA = descriptionFrequency[a] === filteredMatchedEntries.length;
      const appearsInAllB = descriptionFrequency[b] === filteredMatchedEntries.length;
      if (appearsInAllA && !appearsInAllB) return -1;
      if (!appearsInAllA && appearsInAllB) return 1;
      return a.localeCompare(b);
    });
  };

  // Calculate row total for a specific group
  const calculateRowTotal = (group) => {
    let rowTotal = actualInstallationTrainingForGroup(group);
    group.entries.forEach((entry) => {
      if (isTrainingExpenseDisplayRow(entry)) return;
      const amt = installationAssociatedEntryAmount(entry);
      if (amt) {
        rowTotal += amt;
      }
    });
    const tripduration = resolveGroupTripDuration(group, filteredMatchedEntries);
    rowTotal += perdiem * tripduration + averagecost * tripduration;
    return rowTotal;
  };

  const allDescriptions = getAllDescriptions();
  const groupedPersons = groupPersonsByManager;
  return (
    filteredMatchedEntries.length ? (
      <div ref={AssociatedRef} className="bg-white pt-5 pl-2 rounded-md">
        <div className="text-lg font-semibold pb-2">
          <h2>Associated Costs: Installation</h2>
        </div>

        {/* Single Table with Horizontal Layout - No Total Column */}
        <div className="">
          <div className="maintable text-xs  inline-block">
            <div className="flex bg-[#e8e8e8] border-r">
              <div className="p-3 text-left w-40 bg-[#e8e8e8] border-b flex justify-start font-semibold align-middle items-center">
                Task
              </div>
              {filteredMatchedEntries.map((group, index) => (
                <div
                  key={index}
                  className="px-2 py-2 capitalize w-40 border-b bg-[#e8e8e8] flex justify-center font-semibold align-middle items-center text-center"
                >
                  {getPersonDisplayName(group.taskownerid, groupedPersons)}
                </div>
              ))}
            </div>
            <div className="border">
              {/* Total Row */}
              <div className="flex text-xs font-semibold bg-white border-r">
                <div className="p-3 w-40 flex justify-start align-middle border-b items-center font-semibold">
                  Total ({currency})
                </div>
                {filteredMatchedEntries.map((group, index) => (
                  <div
                    key={index}
                    className="px-2 py-2 text-xs  w-40 flex justify-center border-b align-middle items-center font-semibold"
                  >
                    {formatMoneyComma(calculateRowTotal(group))}
                  </div>
                ))}
              </div>
              {/* Trip Duration Row */}
              <div className="flex bg-white border-r">
                <div className="p-3 w-40 flex justify-start border-b align-middle items-center">
                  Trip Duration (Days)
                </div>
                {filteredMatchedEntries.map((group, index) => {
                  const trip = resolveGroupTripDuration(
                    group,
                    filteredMatchedEntries
                  );
                  return (
                    <div
                      key={index}
                      className="px-2 py-2 w-40 flex justify-center border-b align-middle items-center"
                    >
                      {trip > 0 ? trip : "-"}
                    </div>
                  );
                })}
              </div>

              {/* Dynamic Description Rows */}
              {/* Dynamic Description Rows */}
              {/* Dynamic Description Rows */}
              <div className="narrow-scrollbar descriptionbox max-h-[196px] overflow-y-auto inline-block border-r">
                {allDescriptions.map((description, descIndex) => (
                  <div key={descIndex} className={`flex `}>
                    <div
                      className={`px-2 py-2 pl-3 w-40 min-h-[48px] flex justify-start border-b align-middle items-center bg-white `}
                    >
                      {description}
                    </div>
                    {filteredMatchedEntries.map((group, groupIndex) => {
                      const rowIsTraining = isTrainingExpenseDisplayRow({ description });
                      const amount = rowIsTraining
                        ? actualInstallationTrainingForGroup(group)
                        : (() => {
                            const matchedEntry = group.entries.find(
                              (entry) =>
                                canonicalInstallationAssociatedTaskDescription(
                                  entry.description,
                                  currency
                                ) === description
                            );
                            return matchedEntry ? installationAssociatedEntryAmount(matchedEntry) : 0;
                          })();

                      return (
                        <div
                          key={groupIndex}
                          className={`p-3 w-40 min-h-[48px] flex justify-center border-b align-middle items-center text-center bg-white`}
                        >
                          {amount > 0 ? formatMoneyComma(amount) : "-"}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              {/* Perdiem Row */}
              <div className="flex bg-white border-r">
                <div className="p-3 w-40 flex justify-start border-b align-middle items-center">
                  Perdiem ({currency})
                </div>
                {filteredMatchedEntries.map((group, index) => {
                  const trip = resolveGroupTripDuration(
                    group,
                    filteredMatchedEntries
                  );
                  return (
                    <div
                      key={index}
                      className="px-2 py-2  w-40 flex justify-center border-b align-middle items-center"
                    >
                      {USAComma(perdiem * trip)}
                    </div>
                  );
                })}
              </div>

              {/* Average Cost Row */}
              <div className="flex bg-white border-r">
                <div className="p-3  w-40 flex justify-start  border-b align-middle items-center">
                  Average Cost ({currency})
                </div>
                {filteredMatchedEntries.map((group, index) => {
                  const trip = resolveGroupTripDuration(
                    group,
                    filteredMatchedEntries
                  );
                  return (
                    <div
                      key={index}
                      className="px-2 py-2   w-40 flex justify-center border-b align-middle items-center"
                    >
                      {USAComma(averagecost * trip)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <>
        <div ref={AssociatedRef} className="bg-white pt-5 pl-2 rounded-md">
          <div className="text-lg font-semibold pb-2">
            <h2>Associated Costs: Installation</h2>
          </div>

          <div className="w-full bg-white flex h-10 border-b">
            <div className="w-full py-3 flex items-center justify-start text-start text-red-500 text-sm font-semibold">
              No data available
            </div>
          </div>
        </div>
      </>
    )
  );
};

export default ActualInstallationTable;
